import React, { useState, useEffect } from 'react';
import { ArrowLeft, Menu } from 'lucide-react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import NavigationMenu from './NavigationMenu';
import { trackEvent } from './MetaPixel';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DynamicPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isHomePage = location.state?.isHomePage || false;
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    fetchPage();
  }, [slug]);

  const fetchPage = async () => {
    try {
      const res = await fetch(`${API_URL}/api/pages/${slug}`);
      if (res.ok) {
        const data = await res.json();
        setPage(data);
        trackEvent('ViewContent', { content_name: data.title, content_category: slug });
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#1a2b3c] flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-[#50c878] border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!page) {
    return (
      <div className="min-h-screen bg-[#1a2b3c] flex items-center justify-center text-[#f5f5dc]">
        <p>Pagina no encontrada</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0d1620] text-[#f5f5dc] font-body" data-testid="dynamic-page">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 flex items-center justify-between px-6 py-4">
        <button
          onClick={() => setMenuOpen(true)}
          className="w-12 h-12 rounded-full bg-[#50c878] flex items-center justify-center hover:bg-[#40a0a0] transition-colors"
          data-testid="menu-button"
        >
          <Menu className="w-6 h-6 text-white" />
        </button>
        {!isHomePage && (
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-[#f5f5dc]/60 hover:text-[#f5f5dc] transition-colors font-condensed tracking-wider text-sm uppercase"
            data-testid="back-button"
          >
            <ArrowLeft className="w-4 h-4" /> Volver
          </button>
        )}
      </header>

      <NavigationMenu isOpen={menuOpen} onClose={() => setMenuOpen(false)} />

      {/* Hero Section */}
      {page.heroImage && (
        <div className="relative w-full h-[70vh] overflow-hidden">
          <img
            src={page.heroImage}
            alt={page.title}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0d1620] via-[#0d1620]/40 to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 p-8 lg:p-16">
            <h1 className="font-metro text-5xl sm:text-6xl lg:text-7xl text-[#f5f5dc] drop-shadow-lg" data-testid="page-title">
              {page.title}
            </h1>
          </div>
        </div>
      )}

      {/* Blocks Content */}
      <div className="max-w-4xl mx-auto px-6 py-12 space-y-10">
        {page.blocks.map((block) => {
          switch (block.type) {
            case 'heading':
              return (
                <h2 key={block.id} className="font-metro text-3xl sm:text-4xl text-[#f5f5dc]" data-testid={`block-heading-${block.id}`}>
                  {block.content}
                </h2>
              );
            case 'text':
              return (
                <div key={block.id} className="text-lg text-[#f5f5dc]/75 leading-relaxed whitespace-pre-line" data-testid={`block-text-${block.id}`}>
                  {block.content}
                </div>
              );
            case 'image':
              return (
                <figure key={block.id} className="relative rounded-xl overflow-hidden group" data-testid={`block-image-${block.id}`}>
                  <div className="absolute -inset-2 bg-[#50c878]/10 rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                  <img
                    src={block.imageUrl}
                    alt={block.caption || ''}
                    className="w-full rounded-xl relative z-10"
                  />
                  {block.caption && (
                    <figcaption className="mt-3 text-sm text-[#f5f5dc]/40 font-condensed tracking-wider uppercase text-center relative z-10">
                      {block.caption}
                    </figcaption>
                  )}
                </figure>
              );
            case 'quote':
              return (
                <blockquote key={block.id} className="relative pl-6 border-l-4 border-[#50c878] py-4" data-testid={`block-quote-${block.id}`}>
                  <p className="text-xl italic text-[#f5f5dc]/80 leading-relaxed">
                    "{block.content}"
                  </p>
                </blockquote>
              );
            default:
              return null;
          }
        })}
      </div>

      {/* Footer CTA */}
      <div className="text-center py-16 border-t border-[#f5f5dc]/10">
        <p className="font-metro text-3xl text-[#f5f5dc] mb-4">En Cines 15 de Mayo</p>
        <button
          onClick={() => navigate('/entradas')}
          className="px-8 py-3 bg-[#50c878] text-white rounded-full font-condensed tracking-wider uppercase hover:bg-[#40a0a0] transition-colors"
          data-testid="buy-tickets-cta"
        >
          Comprar Entradas
        </button>
      </div>
    </div>
  );
};

export default DynamicPage;
